import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  IconButton,
  Tooltip as MuiTooltip,
  useTheme,
} from "@mui/material";
import { HelpOutline as HelpIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { getAggregateDataSummary } from "../../store/modules/analytics/analyticsActions";
import { AggregationTypesEnum } from "../../types/types";
import { formatCurrencyForStats, getShortMonthName } from "../../utils/common";
import { useDataExplanation } from "../../hooks/useDataExplanation";
import { DataExplanationDialog } from "../Dialogs/DataExplanationDialog";
import {
  PIPELINE_DOT_COLOR,
  analyticsRequestYearIds,
  splitAnalyticsRows,
  useAnalyticsYears,
} from "../../hooks/usePipeline";

interface ChartDataPoint {
  year?: number;
  month?: number;
  quarter?: number;
  totalCount: number | null;
  totalAmount: number | null;
  average: number | null;
  /** "+ Under Contracts" dot: the line value plus Under Contract transactions */
  withUnderContract?: number | null;
  /** "+ Pipeline" dot: the line value (+ Under Contracts) plus Pipeline entries */
  withPipeline?: number | null;
  displayPeriod?: string;
  periodKey?: string;
}

interface AggregateRow {
  yearId?: number;
  year?: number;
  month?: number;
  quarter?: number;
  totalCount: number;
  totalAmount: number;
  isPipeline?: boolean;
  isUnderContract?: boolean;
}

interface Props {
  aggregationType: AggregationTypesEnum;
  mode: "count" | "amount" | "average";
  selectedYearIds?: number[];
  timePeriod: "quarter" | "month" | "year";
  selectedTypeIds: number[];
  coAgentIds?: number[];
  /** Adds a "+ Under Contracts" dot per period and takes those out of the line */
  includeUnderContracts?: boolean;
}

const AggregateTrendChart: React.FC<Props> = ({
  aggregationType,
  mode,
  selectedYearIds,
  timePeriod,
  selectedTypeIds,
  coAgentIds,
  includeUnderContracts = false,
}) => {
  const theme = useTheme();

  const {
    explainData,
    explanation,
    isLoading,
    isOpen,
    closeExplanation,
    dailyLimitInfo,
  } = useDataExplanation({
    onError: (error) => console.error("Data explanation error:", error),
  });

  const { years: yearFolders, pipelineYearId } = useAnalyticsYears();
  const requestYearIds = analyticsRequestYearIds(
    selectedYearIds ?? [],
    yearFolders,
    pipelineYearId,
    includeUnderContracts,
  );

  const { data } = useQuery({
    queryKey: [
      "aggregateDataSummary",
      requestYearIds,
      selectedTypeIds,
      aggregationType,
      coAgentIds,
    ],
    queryFn: async (): Promise<AggregateRow[]> => {
      const result = await getAggregateDataSummary(
        requestYearIds,
        selectedTypeIds,
        aggregationType,
        coAgentIds,
      );
      return (
        result?.map((item) => ({
          yearId: item.yearId,
          year: item.year,
          month: item.month,
          quarter: item.quarter,
          totalCount: item.totalCount || 0,
          totalAmount: item.totalAmount || 0,
          isPipeline: item.isPipeline,
          isUnderContract: item.isUnderContract,
        })) || []
      );
    },
    refetchOnWindowFocus: false,
  });

  const split = useMemo(
    () =>
      splitAnalyticsRows(data, {
        years: yearFolders,
        selectedYearIds,
        pipelineYearId,
        includeUnderContracts,
      }),
    [
      data,
      yearFolders,
      selectedYearIds,
      pipelineYearId,
      includeUnderContracts,
    ],
  );
  const years = split.chartYears;
  const isSingleYearSelected = split.isSingleYear;

  // Generate all periods for the selected years and time period
  const allPeriods = useMemo(() => {
    // Quarters or months of the one year; Yearly (e.g. only Pipeline checked) is one point per year
    if (isSingleYearSelected && timePeriod !== "year") {
      const yearId = split.periodYearIds[0];
      const periodCount = timePeriod === "month" ? 12 : 4;
      return Array.from({ length: periodCount }, (_, i) => ({
        yearId,
        [timePeriod]: i + 1,
        periodKey: `${yearId}-${timePeriod}-${i + 1}`,
      }));
    } else {
      // For multiple years, generate all combinations of years and periods
      const periods: any[] = [];
      const relevantYears = years.filter((y) =>
        split.periodYearIds.includes(y.id!),
      );

      // Sort years by year value (ascending) to ensure consistent chronological order
      // Create a copy of the array before sorting to avoid mutating the original
      const sortedYears = [...relevantYears].sort(
        (a, b) => (a.year || 0) - (b.year || 0),
      );

      sortedYears.forEach((year) => {
        if (timePeriod === "year") {
          periods.push({
            yearId: year.id,
            year: year.year,
            periodKey: `${year.id}-year`,
          });
        } else {
          const periodCount = timePeriod === "month" ? 12 : 4;
          for (let i = 1; i <= periodCount; i++) {
            periods.push({
              yearId: year.id,
              [timePeriod]: i,
              periodKey: `${year.id}-${timePeriod}-${i}`,
            });
          }
        }
      });
      return periods;
    }
  }, [split, timePeriod, years, isSingleYearSelected]);

  const mergedData: ChartDataPoint[] = useMemo(() => {
    return allPeriods
      .map((period: any) => {
        // Sum the rows of one set (line, Under Contracts, Pipeline) in this period
        const sumPeriod = (rows: AggregateRow[]) => {
          const matching = rows.filter(
            (d) =>
              d.yearId === period.yearId &&
              (timePeriod === "year" || d[timePeriod] === period[timePeriod]),
          );
          return {
            count: matching.reduce((sum, item) => sum + item.totalCount, 0),
            amount: matching.reduce((sum, item) => sum + item.totalAmount, 0),
          };
        };
        const { count: totalCount, amount: totalAmount } = sumPeriod(
          split.actual,
        );
        const underContract = sumPeriod(split.underContract);
        const pipeline = sumPeriod(split.pipeline);

        // The plotted value for a count and amount, the same way the line is plotted
        const plotted = (count: number, amount: number) =>
          aggregationType === AggregationTypesEnum.Average
            ? mode === "count"
              ? count
              : count > 0
                ? amount / count
                : 0
            : mode === "count"
              ? count
              : amount;

        // Get the year object for display purposes
        const yearObj = years.find((y) => y.id === period.yearId);
        const yearValue = yearObj?.year ?? period.yearId;

        // Generate display period for multi-year scenarios
        let displayPeriod: string;
        if (timePeriod === "year") {
          displayPeriod = `${yearValue}`;
        } else if (isSingleYearSelected) {
          displayPeriod =
            timePeriod === "month"
              ? getShortMonthName(period[timePeriod])
              : `Q${period[timePeriod]}`;
        } else {
          displayPeriod =
            timePeriod === "month"
              ? `${yearValue} ${getShortMonthName(period[timePeriod])}`
              : `${yearValue} Q${period[timePeriod]}`;
        }

        const average = totalCount > 0 ? totalAmount / totalCount : 0;
        // A year with no folder yet only has dots
        const noLine = split.isDotsOnlyYear(period.yearId);
        const ucCount = totalCount + underContract.count;
        const ucAmount = totalAmount + underContract.amount;

        return {
          year: period.yearId,
          month: period.month,
          quarter: period.quarter,
          totalCount: noLine ? null : totalCount,
          totalAmount: noLine ? null : totalAmount,
          average: noLine ? null : mode === "count" ? totalCount : average,
          withUnderContract:
            underContract.count > 0 ? plotted(ucCount, ucAmount) : null,
          withPipeline:
            pipeline.count > 0
              ? plotted(ucCount + pipeline.count, ucAmount + pipeline.amount)
              : null,
          displayPeriod,
          periodKey: period.periodKey,
        };
      })
      .sort((a: any, b: any) => {
        // Sort by year first, then by period
        const yearA = years.find((y) => y.id === a.year)?.year || a.year;
        const yearB = years.find((y) => y.id === b.year)?.year || b.year;

        if (yearA !== yearB) {
          return yearA - yearB;
        }

        if (timePeriod === "year") return 0;

        const periodA = timePeriod === "month" ? a.month : a.quarter;
        const periodB = timePeriod === "month" ? b.month : b.quarter;

        return (periodA || 0) - (periodB || 0);
      });
  }, [
    allPeriods,
    split,
    years,
    timePeriod,
    isSingleYearSelected,
    mode,
    aggregationType,
  ]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const dataItem: ChartDataPoint = payload[0].payload;
    const formatValue = (value: number) =>
      mode === "count"
        ? aggregationType === AggregationTypesEnum.Total
          ? value.toFixed(0)
          : value.toFixed(2)
        : formatCurrencyForStats(value);
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "#fff",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "4px",
        }}
      >
        <p style={{ fontWeight: "bold", marginBottom: "5px" }}>
          {dataItem.displayPeriod}
        </p>
        {payload
          .filter(
            (entry: any) => entry.value !== null && entry.value !== undefined,
          )
          .map((entry: any) => (
            <p key={entry.dataKey} style={{ color: entry.color }}>
              {entry.name}: {formatValue(entry.value)}
            </p>
          ))}
      </div>
    );
  };

  const yAxisLabel =
    aggregationType === AggregationTypesEnum.Total
      ? mode === "count"
        ? "Total Units"
        : "Total Volume"
      : mode === "count"
        ? "Average Units"
        : "Average Price";

  const handleExplainData = () => {
    const aggregationTypeText =
      aggregationType === AggregationTypesEnum.Total ? "Total" : "Average";
    const selectedYearText =
      (selectedYearIds ?? []).length > 0
        ? selectedYearIds!
            .filter((id) => id !== pipelineYearId)
            .map((id) => years.find((y) => y.id === id)?.year)
            .join(", ")
        : "all years";

    explainData(
      mergedData,
      "aggregate",
      mode,
      timePeriod,
      selectedYearText,
      undefined,
      undefined,
      undefined,
      aggregationTypeText,
    );
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          mt: 2,
        }}
      >
        {/* Header row with title and AI insights button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            px: 1,
          }}
        >
          <Box sx={{ fontWeight: "bold", fontSize: "1rem" }}>
            {/* {aggregationType === AggregationTypesEnum.Total
              ? "Total"
              : "Average"}{" "}
            {mode.charAt(0).toUpperCase() + mode.slice(1)} Trend */}
          </Box>
          <MuiTooltip title="Get AI insights about this trend data">
            <IconButton
              size="small"
              onClick={handleExplainData}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              <HelpIcon fontSize="small" />
            </IconButton>
          </MuiTooltip>
        </Box>

        {/* Chart container */}
        <Box
          sx={{
            width: "100%",
            height: 400,
            padding: 1,
            paddingRight: 2,
            overflow: "visible",
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={mergedData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: isSingleYearSelected ? 50 : 80,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="displayPeriod"
                axisLine={false}
                tick={{ fontSize: 14, fontFamily: "Segoe UI" }}
                tickLine={false}
                type="category"
                interval={0}
                angle={isSingleYearSelected ? 0 : -45}
                textAnchor={isSingleYearSelected ? "middle" : "end"}
                height={isSingleYearSelected ? 50 : 80}
                label={{
                  value: isSingleYearSelected
                    ? timePeriod === "month"
                      ? "Month"
                      : "Quarter"
                    : timePeriod === "year"
                      ? "Year"
                      : `${
                          timePeriod.charAt(0).toUpperCase() +
                          timePeriod.slice(1)
                        }s`,
                  position: "insideBottomRight",
                  offset: -5,
                }}
              />
              <YAxis
                fontSize={13}
                domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
                tickFormatter={(value) =>
                  mode === "count"
                    ? `${value.toFixed(0)}`
                    : formatCurrencyForStats(value)
                }
                label={{
                  position: "insideLeft",
                  offset: 0,
                }}
                width={70}
                allowDataOverflow={true}
                allowDecimals={false}
                padding={{ top: 20 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="top" align="left" />
              <Line
                type="monotone"
                dataKey={
                  aggregationType === AggregationTypesEnum.Average
                    ? "average"
                    : mode === "count"
                      ? "totalCount"
                      : "totalAmount"
                }
                stroke="#8884d8"
                strokeWidth={3}
                activeDot={{ r: 8 }}
                name={yAxisLabel}
                connectNulls={true}
              />
              {split.includeUnderContracts && (
                <Line
                  dataKey="withUnderContract"
                  stroke={theme.palette.warning.main}
                  strokeWidth={0}
                  dot={{
                    fill: theme.palette.warning.main,
                    stroke: "none",
                    r: 6,
                  }}
                  activeDot={{ fill: theme.palette.warning.main, r: 8 }}
                  name="+ Under Contracts"
                  connectNulls={false}
                  legendType="circle"
                />
              )}
              {split.includePipeline && (
                <Line
                  dataKey="withPipeline"
                  stroke={PIPELINE_DOT_COLOR}
                  strokeWidth={0}
                  dot={{ fill: PIPELINE_DOT_COLOR, stroke: "none", r: 6 }}
                  activeDot={{ fill: PIPELINE_DOT_COLOR, r: 8 }}
                  name="+ Pipeline"
                  connectNulls={false}
                  legendType="circle"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <DataExplanationDialog
        open={isOpen}
        onClose={closeExplanation}
        explanation={explanation}
        isLoading={isLoading}
        title={`${
          aggregationType === AggregationTypesEnum.Total ? "Total" : "Average"
        } ${mode.charAt(0).toUpperCase() + mode.slice(1)} Trend Analysis`}
        dailyLimitInfo={dailyLimitInfo}
      />
    </>
  );
};

export default AggregateTrendChart;
